import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useStockMarketBatch } from '../../hooks/useStockMarket';
import { useCurrency } from '../../context/CurrencyContext';
import { SUPPORTED_STOCKS, tickerFromId, ASSET_BADGE } from '../../utils/stocks';
import { formatPercent } from '../../utils/formatters';
import { Search, RefreshCw, AlertCircle, TrendingUp, TrendingDown, ChevronLeft, ChevronRight } from 'lucide-react';

// 100 tickers per page — one page = a single /api/stock-quotes batch request.
const PER_PAGE = 100;
const TOTAL_PAGES = Math.max(1, Math.ceil(SUPPORTED_STOCKS.length / PER_PAGE));

// ─── Stock/ETF icon (coloured circle — stocks have no image) ──────────────────
// Mirrors the StockCircle used in CoinSelector / AssetComparison: ETF purple,
// equity blue, with the TrendingUp glyph.
function StockIcon({ type, size = 'w-9 h-9' }: { type: 'stock' | 'etf'; size?: string }) {
  const etf   = type === 'etf';
  const color = etf ? '#8B5CF6' : '#3B82F6';
  const bg    = etf ? '#8B5CF615' : '#3B82F615';
  const bdr   = etf ? '#8B5CF640' : '#3B82F640';
  return (
    <div
      className={`${size} rounded-full flex items-center justify-center flex-shrink-0`}
      style={{ backgroundColor: bg, border: `1.5px solid ${bdr}` }}
    >
      <TrendingUp style={{ color, width: '50%', height: '50%' }} />
    </div>
  );
}

// ─── Change cell (local copy — keeps MarketTable untouched) ───────────────────
function ChangeCell({ value }: { value: number }) {
  const up = value >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-sm font-medium ${up ? 'text-emerald-400' : 'text-red-400'}`}>
      {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {formatPercent(value)}
    </span>
  );
}

function Badge({ type }: { type: 'stock' | 'etf' }) {
  const b = ASSET_BADGE[type];
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${b.className}`}>{b.label}</span>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export function StockMarketTable() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Page lives in the URL (?stkPage=N) — a distinct key from crypto's ?mktPage,
  // because both Market panels stay mounted at once (see MarketView). Missing /
  // invalid / out-of-range → page 1.
  const rawPage = Number(searchParams.get('stkPage'));
  const page = Number.isInteger(rawPage) && rawPage >= 1 && rawPage <= TOTAL_PAGES ? rawPage : 1;

  const setPage = (next: number) => {
    const clamped = Math.min(Math.max(1, next), TOTAL_PAGES);
    setSearchParams((prev) => {
      const n = new URLSearchParams(prev);
      if (clamped === 1) n.delete('stkPage'); // keep the URL clean on page 1
      else n.set('stkPage', String(clamped));
      return n;
    });
  };

  // The slice of the universe shown on this page — only these are fetched.
  const pageStocks = useMemo(
    () => SUPPORTED_STOCKS.slice((page - 1) * PER_PAGE, page * PER_PAGE),
    [page]
  );
  const pageIds = useMemo(() => pageStocks.map((s) => s.id), [pageStocks]);

  const { data, loading, error, lastUpdated, refresh } = useStockMarketBatch(pageIds);
  const { fmt } = useCurrency();
  const [query, setQuery] = useState('');

  // Scroll back to the top of the panel when the page changes (skip mount).
  const topRef   = useRef<HTMLDivElement>(null);
  const didMount = useRef(false);
  useEffect(() => {
    if (!didMount.current) { didMount.current = true; return; }
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [page]);

  // Return search for asset links so Back lands on the right stocks page + sub-tab.
  const returnSearch = page > 1 ? `?tab=market&sub=stocks&stkPage=${page}` : '?tab=market&sub=stocks';

  // Client-side search over the CURRENT page only (matches the crypto tab).
  const rows = useMemo(() => {
    const base = pageStocks.map((s) => ({
      id:     s.id,
      symbol: tickerFromId(s.id),
      name:   s.name,
      type:   s.type,
    }));
    const q = query.trim().toLowerCase();
    if (!q) return base;
    return base.filter((r) => r.name.toLowerCase().includes(q) || r.symbol.toLowerCase().includes(q));
  }, [pageStocks, query]);

  const hasData = Object.keys(data).length > 0;

  const rangeStart = (page - 1) * PER_PAGE + 1;
  const rangeEnd   = (page - 1) * PER_PAGE + pageStocks.length;

  const timeStr = lastUpdated
    ? lastUpdated.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : null;

  // 52-week range cell: only shown when both bounds converted successfully.
  const rangeText = (id: string): string => {
    const e = data[id];
    if (!e || e.week52LowUsd == null || e.week52HighUsd == null) return '—';
    return `${fmt(e.week52LowUsd)} – ${fmt(e.week52HighUsd)}`;
  };

  return (
    <div ref={topRef} className="space-y-5">
      {/* ── Toolbar ── */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or symbol…"
            className="w-full bg-slate-800 border border-slate-700 rounded-xl pl-9 pr-4 py-2.5 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-slate-500 transition-colors"
          />
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {timeStr && (
            <span className="text-xs text-slate-500">
              Updated {timeStr}
              {error && <span className="text-yellow-500 ml-1">·</span>}
            </span>
          )}
          <button
            onClick={refresh}
            disabled={loading}
            className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 bg-slate-800 hover:bg-slate-700 border border-slate-700 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/20 rounded-xl px-4 py-2.5">
          <AlertCircle className="w-4 h-4 text-yellow-400 flex-shrink-0" />
          <p className="text-xs text-yellow-400">{error}</p>
        </div>
      )}

      {loading && !hasData ? (
        <div className="border-t border-slate-700 flex flex-col items-center justify-center py-20 gap-3">
          <RefreshCw className="w-6 h-6 text-white animate-spin" />
          <span className="text-sm text-slate-500">Loading stock prices…</span>
        </div>
      ) : (
        <>
          {/* Desktop table (≥ md) */}
          <div className="hidden md:block overflow-x-auto border-t border-slate-700">
            <table className="w-full min-w-[720px]">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="px-4 py-3.5 text-left label-caps text-slate-500 w-12 pl-0">#</th>
                  <th className="px-4 py-3.5 text-left label-caps text-slate-500">Name</th>
                  <th className="px-4 py-3.5 text-right label-caps text-slate-500">Price</th>
                  <th className="px-4 py-3.5 text-right label-caps text-slate-500">24h</th>
                  <th className="px-4 py-3.5 text-right label-caps text-slate-500 pr-0">52-Week Range</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/60">
                {rows.map((r, i) => {
                  const e = data[r.id];
                  return (
                    <tr
                      key={r.id}
                      onClick={() => navigate(`/coin/${r.id}`, { state: { from: 'market', fromSearch: returnSearch } })}
                      className="hover:bg-slate-800/30 transition-colors cursor-pointer"
                    >
                      {/* Index within the page (no market-cap rank exists for stocks) */}
                      <td className="px-5 py-5 pl-0">
                        <span className="text-[14px] text-slate-500">{rangeStart + i}</span>
                      </td>

                      {/* Name / ticker + badge */}
                      <td className="px-5 py-5">
                        <div className="flex items-center gap-3.5">
                          <StockIcon type={r.type} />
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-[14px] font-medium text-slate-100">{r.name}</p>
                              <Badge type={r.type} />
                            </div>
                            <p className="text-xs text-slate-500 mt-0.5">{r.symbol}</p>
                          </div>
                        </div>
                      </td>

                      {/* Price */}
                      <td className="px-5 py-5 text-right">
                        <span className="text-[14px] font-medium text-slate-200">
                          {e && e.usd > 0 ? fmt(e.usd) : '—'}
                        </span>
                      </td>

                      {/* 24h */}
                      <td className="px-5 py-5 text-right">
                        {e && e.usd > 0 ? <ChangeCell value={e.usd_24h_change} /> : <span className="text-slate-600">—</span>}
                      </td>

                      {/* 52-week range */}
                      <td className="px-5 py-5 pr-0 text-right">
                        <span className="text-[14px] text-slate-400 tabular-nums">{rangeText(r.id)}</span>
                      </td>
                    </tr>
                  );
                })}

                {rows.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-16 text-center">
                      <p className="text-slate-500 text-sm">No assets match "{query}"</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile list (< md) — divided rows */}
          <div className="md:hidden border-t border-slate-700">
            {rows.length === 0 ? (
              <p className="py-16 text-center text-slate-500 text-sm">No assets match "{query}"</p>
            ) : (
              <div className="divide-y divide-slate-700/40">
                {rows.map((r, i) => {
                  const e = data[r.id];
                  return (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => navigate(`/coin/${r.id}`, { state: { from: 'market', fromSearch: returnSearch } })}
                      className="w-full flex items-center gap-3 py-3 text-left transition-colors active:bg-slate-800/30"
                    >
                      <span className="w-6 flex-shrink-0 text-right text-[12px] text-slate-500 tabular-nums">
                        {rangeStart + i}
                      </span>
                      <StockIcon type={r.type} size="w-8 h-8" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <p className="text-[14px] font-medium text-slate-100 truncate">{r.name}</p>
                          <Badge type={r.type} />
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">{r.symbol}</p>
                      </div>
                      <div className="flex-shrink-0 text-right">
                        <p className="text-[14px] font-semibold text-slate-100">
                          {e && e.usd > 0 ? fmt(e.usd) : '—'}
                        </p>
                        <div className="mt-0.5 flex justify-end">
                          {e && e.usd > 0 ? <ChangeCell value={e.usd_24h_change} /> : <span className="text-slate-600 text-sm">—</span>}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* Search matched nothing on this page — hint to try another (search is per-page) */}
      {query.trim() && rows.length === 0 && (
        <p className="text-xs text-slate-600 text-center">
          No matches on page {page}. Try another page.
        </p>
      )}

      {/* Pagination — 100 per page */}
      {TOTAL_PAGES > 1 && (
        <div className="flex items-center justify-center gap-4">
          <button
            type="button"
            onClick={() => setPage(page - 1)}
            disabled={page <= 1}
            className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 bg-slate-800 hover:bg-slate-700 border border-slate-700 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            Previous
          </button>
          <span className="text-xs text-slate-500 tabular-nums">Page {page} of {TOTAL_PAGES}</span>
          <button
            type="button"
            onClick={() => setPage(page + 1)}
            disabled={page >= TOTAL_PAGES}
            className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 bg-slate-800 hover:bg-slate-700 border border-slate-700 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {!loading || hasData ? (
        <p className="text-xs text-slate-600 text-center">
          Showing {rangeStart}–{rangeEnd} of {SUPPORTED_STOCKS.length} · Prices from Yahoo Finance · Auto-refreshes every 60s
        </p>
      ) : null}
    </div>
  );
}
